'use client';

import type { FormSchema } from '~/landing/app/(garance)/cagnup/schema';

import { Card, CardContent, Icon, Typography } from '@polyseme/design-system';
import { formatCurrency } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import {
  calculateSavingsPerBudgetCategory,
  calculateTotalCashbackForAllCategories,
  simulateProjections,
} from '~/landing/app/(garance)/cagnup/calculations';
import { SimulationForm } from '~/landing/app/(garance)/cagnup/components';
import { getRateByRiskProfile } from '~/landing/core/simulator/future-savings-planner';

const rate = getRateByRiskProfile('DYNAMISM');

export const SimulationSection = () => {
  const t = useTranslations('landing.cagnup');
  const form = useFormContext<FormSchema>();

  const [budget, family] = [form.watch('budget'), form.watch('family')];

  const backToSchoolBudget = calculateTotalCashbackForAllCategories(family);

  const savingsPerCategory = calculateSavingsPerBudgetCategory(budget);

  const projections = simulateProjections({
    yearlyBudget: {
      food: budget.food || 0,
      housing: budget.housing || 0,
      shopping: budget.shopping || 0,
      subscriptions: budget.subscriptions || 0,
    },
    backToSchoolBudget,
    rate,
  });

  const lastProjection = projections[projections.length - 1];
  const investmentMade = lastProjection?.investmentMade ?? 0;
  const savingPerYear = investmentMade / projections.length;

  return (
    <section className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 py-16 md:px-10">
      <Card className="hidden md:block">
        <CardContent className="space-y-6 p-6">
          <SimulationForm
            projections={projections}
            rate={rate}
            savingsPerCategory={savingsPerCategory}
          />
        </CardContent>
      </Card>
      <div className="space-y-6 md:hidden">
        <SimulationForm
          projections={projections}
          rate={rate}
          savingsPerCategory={savingsPerCategory}
        />
      </div>
      <Card className="border-2 border-[#89C1C7] bg-[#E9F5F7]">
        <CardContent className="flex h-full flex-col items-center p-2 text-center md:justify-center md:p-6 xl:justify-start">
          <Typography className="mb-2" variant="title-4">
            {t('savings.title')}
          </Typography>
          <Typography className="mb-6 text-muted-foreground" variant="body">
            {t('savings.subtitle')}
          </Typography>
          <div className="flex items-end gap-2 md:gap-6">
            <div>
              <Typography variant="body">{t('savings.eachYear')}</Typography>
              <Typography variant="title-2">
                {formatCurrency(savingPerYear)}
              </Typography>
            </div>
            <div className="grid size-12 shrink-0 translate-y-1 place-items-center rounded-full bg-[#B6DCE3]">
              <Icon className="size-5" name="arrow-right" />
            </div>
            <div>
              <Typography variant="body">
                {t('savings.atEnd', {
                  duration: String(projections.length),
                })}
              </Typography>
              <Typography variant="title-2">
                {formatCurrency(investmentMade)}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
